import { Search, DraftingCompass, Wrench, LifeBuoy, Phone, Mail, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import { Eyebrow, GradientText } from "@/components/Brand";
import { Reveal, Counter } from "@/lib/motion";
import { SITE, IMAGES } from "@/lib/site";

const APPROACH = [
  { icon: Search, title: "Assess", text: "Understand the property's layout and comfort requirements." },
  { icon: DraftingCompass, title: "Design", text: "Select the appropriate system type and capacity." },
  { icon: Wrench, title: "Install", text: "Coordinate professional installation and commissioning." },
  { icon: LifeBuoy, title: "Support", text: "Provide a clear contact point after installation." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Airconditioning Victoria | Serving Victoria Since 2000"
        description="Airconditioning Victoria has completed 6,500+ air conditioning installations across Melbourne and Victoria since 2000. Learn about our approach."
        path="/about"
      />
      <PageHero
        eyebrow="About Airconditioning Victoria"
        titleLines={["Victorian comfort", <GradientText key="g">since 2000.</GradientText>]}
        intro="Airconditioning Victoria has been helping customers create more comfortable indoor spaces for more than 25 years."
        ctaText="Talk to Our Team"
        image="/assets/about-hero.webp"
        imageAlt="Airconditioning Victoria technician installing a Mitsubishi Electric split system"
      />

      {/* STORY */}
      <section className="bg-offwhite py-24 md:py-32" data-testid="about-story-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Our Story</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              More than <GradientText>6,500 installations</GradientText> and counting.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mutedblue">
              Airconditioning Victoria began operating in 2000 with a focus on practical, reliable
              air conditioning solutions for Victorian properties. Since then, more than 6,500 air
              conditioning installations have been completed across a range of homes and properties.
            </p>
            <p className="mt-4 text-base leading-relaxed text-mutedblue">
              Air conditioning technology has changed considerably over that time, but our core
              approach remains simple: understand the property, recommend an appropriate system, and
              complete the installation professionally.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-6">
              <div className="rounded-[20px] border border-cardline bg-white p-8 text-center shadow-[0_8px_32px_rgba(5,59,117,0.04)]">
                <p className="font-display text-4xl font-extrabold text-gradient-brand md:text-5xl">
                  <Counter value={6500} suffix="+" />
                </p>
                <p className="mt-2 font-display text-xs font-bold uppercase tracking-[0.15em] text-navy">Installations</p>
              </div>
              <div className="rounded-[20px] border border-cardline bg-white p-8 text-center shadow-[0_8px_32px_rgba(5,59,117,0.04)]">
                <p className="font-display text-4xl font-extrabold text-gradient-brand md:text-5xl">
                  <Counter value={25} suffix="+" />
                </p>
                <p className="mt-2 font-display text-xs font-bold uppercase tracking-[0.15em] text-navy">Years Experience</p>
              </div>
              <div className="col-span-2 overflow-hidden rounded-[20px] border border-cardline shadow-[0_8px_32px_rgba(5,59,117,0.04)]">
                <img
                  src={IMAGES.livingRoom}
                  alt="Comfortable Melbourne living room with air conditioning"
                  className="h-48 w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* APPROACH */}
      <section className="bg-white py-24 md:py-32" data-testid="about-approach-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-2xl">
            <Eyebrow>Our Approach</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Simple, thorough, <GradientText>professional.</GradientText>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {APPROACH.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.1}>
                <div className="group h-full rounded-[20px] border border-cardline bg-white p-7 shadow-[0_8px_32px_rgba(5,59,117,0.04)] transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan">
                  <span className="font-display text-5xl font-extrabold text-mist transition-colors duration-300 group-hover:text-ice">
                    0{i + 1}
                  </span>
                  <span className="mt-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-ice">
                    <a.icon className="h-5 w-5 text-brand-blue" />
                  </span>
                  <h3 className="mt-4 font-display text-sm font-bold uppercase tracking-[0.18em] text-navy">{a.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mutedblue">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-cloud py-24 md:py-32" data-testid="about-mission-section">
        <div className="mx-auto max-w-4xl px-5 text-center">
          <Reveal>
            <Eyebrow className="justify-center">Our Mission</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Comfort without making air conditioning complicated.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-mutedblue md:text-lg">
              Our goal is to make choosing a new air conditioning system straightforward. We focus
              on clear advice, suitable equipment and installation solutions designed around the
              individual property.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMPANY DETAILS */}
      <section className="bg-white py-24 md:py-32" data-testid="about-details-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-2xl">
            <Eyebrow>Company Details</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Airconditioning Victoria Pty. Ltd.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <dl className="grid gap-px overflow-hidden rounded-[20px] border border-cardline bg-line sm:grid-cols-2 lg:grid-cols-3" data-testid="company-details">
              {[
                ["Company", SITE.legalName],
                ["ABN", SITE.abn],
                ["Established", SITE.established],
                ["Installations", "6,500+"],
                ["Address", `${SITE.address1}, ${SITE.address2}`],
                ["Phone", SITE.phone],
                ["Email", SITE.email],
              ].map(([k, v]) => (
                <div key={k} className="bg-white p-6">
                  <dt className="font-display text-xs font-bold uppercase tracking-[0.15em] text-brand-blue">{k}</dt>
                  <dd className="mt-2 text-sm font-medium text-ink">{v}</dd>
                </div>
              ))}
              <div className="flex items-center gap-4 bg-navy p-6 sm:col-span-2 lg:col-span-2">
                <MapPin className="h-5 w-5 shrink-0 text-brand-cyan" />
                <p className="text-sm text-[#D6E7F2]">
                  Based in Narre Warren, servicing Melbourne and surrounding Victorian areas.
                </p>
              </div>
            </dl>
          </Reveal>
        </div>
      </section>

      <FinalCTA
        heading="Talk to a team that's been here since 2000."
        text="Get practical advice on the right air conditioning solution for your Victorian property."
      />
    </>
  );
}
